import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, User, Wand2 } from "lucide-react";
import api from "@/lib/api";

const QUICK_PROMPTS = [
  "Book office rent of ₹45,000 for April from ICICI bank with GST and Admin dimension.",
  "Create a purchase bill for laptops for Mumbai branch and classify under IT assets.",
  "What is the difference between Schedule II depreciation and Income Tax Act depreciation?",
  "Suggest a monthly close checklist for a proprietorship in India.",
  "Explain why my GST liability increased this month.",
];

export default function AIAssistantPanel({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste 🙏 I'm your AI accounting assistant. I can draft journal entries, explain GST, Schedule II depreciation, reconcile books, and analyse anomalies. What would you like to do?" },
  ]);
  const [prompt, setPrompt] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const bottom = useRef(null);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, suggestion]);

  const send = async (text) => {
    const msg = (text ?? prompt).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setPrompt(""); setSending(true); setSuggestion(null);
    try {
      const r = await api.post("/ai/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Error: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setSending(false); }
  };

  const suggestJV = async () => {
    if (!prompt.trim()) return;
    setSending(true); setSuggestion(null);
    try {
      const r = await api.post("/ai/suggest-journal", { prompt });
      setSuggestion(r.data);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Suggestion error: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setSending(false); }
  };

  const [posting, setPosting] = useState(false);
  const postSuggestion = async () => {
    if (!suggestion?.lines?.length) return;
    setPosting(true);
    try {
      // Resolve account_code → account_id
      const accsRes = await api.get("/accounts");
      const byCode = Object.fromEntries(accsRes.data.map((a) => [a.code, a]));
      const lines = suggestion.lines.map((l) => {
        const acc = byCode[l.account_code];
        if (!acc) throw new Error(`Unknown account code ${l.account_code}`);
        return { account_id: acc.id, debit: Number(l.debit || 0), credit: Number(l.credit || 0),
                 narration: `${acc.code} ${acc.name}` };
      });
      const total = lines.reduce((s, l) => s + l.debit, 0);
      if (total <= 0) throw new Error("Voucher totals to zero");
      const branches = await api.get("/branches");
      await api.post("/vouchers", {
        voucher_type: "journal",
        date: new Date().toISOString().slice(0, 10),
        branch_id: branches.data[0]?.id,
        narration: suggestion.narration || "AI-drafted entry",
        lines,
      });
      setMessages((m) => [...m, { role: "assistant", content: `✅ Posted journal voucher: ${suggestion.narration}` }]);
      setSuggestion(null); setPrompt("");
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Post failed: " + (e?.response?.data?.detail || e.message) }]);
    } finally { setPosting(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col rounded-none border-l">
        <SheetHeader className="border-b p-4 space-y-1">
          <SheetTitle className="flex items-center gap-2 font-heading text-base">
            <Sparkles className="w-4 h-4 text-accent" /> AI Accounting Assistant
          </SheetTitle>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">GPT-5.2 · India context aware · Suggestions require your confirmation</div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {messages.length === 1 && (
              <div className="space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Try</div>
                {QUICK_PROMPTS.map((qp) => (
                  <button
                    key={qp}
                    onClick={() => send(qp)}
                    data-testid="ai-quick-prompt"
                    className="w-full text-left text-xs p-2.5 border rounded-sm hover:bg-muted transition-colors"
                  >{qp}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <div className="w-6 h-6 rounded-sm bg-accent/15 text-accent flex items-center justify-center shrink-0"><Sparkles className="w-3.5 h-3.5" /></div>}
                <div className={`px-3 py-2 rounded-sm text-[13px] whitespace-pre-wrap max-w-[85%] leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted/60 border"}`}>
                  {m.content}
                </div>
                {m.role === "user" && <div className="w-6 h-6 rounded-sm bg-muted flex items-center justify-center shrink-0"><User className="w-3.5 h-3.5" /></div>}
              </div>
            ))}
            {sending && <div className="text-xs text-muted-foreground pl-8 flex items-center gap-2"><span className="saffron-dot animate-pulse" /> Thinking…</div>}

            {suggestion && (
              <div className="border border-accent/40 rounded-sm p-3 bg-accent/5 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">AI JV Draft · confidence {Math.round((suggestion.confidence || 0) * 100)}%</div>
                </div>
                <div className="text-xs italic text-muted-foreground">{suggestion.narration}</div>
                <table className="w-full text-xs tabular-nums">
                  <thead>
                    <tr className="text-[10px] uppercase text-muted-foreground">
                      <th className="text-left py-1">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(suggestion.lines || []).map((ln, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-1"><span className="font-mono text-[11px]">{ln.account_code}</span> {ln.account_name}</td>
                        <td className="text-right">{ln.debit ? `₹${Number(ln.debit).toLocaleString("en-IN")}` : ""}</td>
                        <td className="text-right">{ln.credit ? `₹${Number(ln.credit).toLocaleString("en-IN")}` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {suggestion.reasoning && <div className="text-[10px] text-muted-foreground border-t pt-2">Reasoning: {suggestion.reasoning}</div>}
                <div className="flex items-center justify-between gap-2 border-t pt-2">
                  <div className="text-[10px] uppercase font-mono text-warning flex-1">User confirmation required — nothing posted yet.</div>
                  <Button size="sm" onClick={postSuggestion} disabled={posting} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 shrink-0" data-testid="ai-post-jv">
                    {posting ? "Posting…" : "✓ Accept & Post"}
                  </Button>
                </div>
              </div>
            )}
            <div ref={bottom} />
          </div>
        </ScrollArea>

        <div className="border-t p-3 space-y-2">
          <div className="flex gap-2">
            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask, or describe a transaction…"
              data-testid="ai-prompt-input"
              className="rounded-sm"
            />
            <Button size="sm" onClick={() => send()} disabled={sending} data-testid="ai-send" className="rounded-sm">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={suggestJV} disabled={sending || !prompt.trim()} data-testid="ai-suggest-jv" className="rounded-sm gap-1.5 flex-1">
              <Wand2 className="w-3.5 h-3.5" /> Suggest Journal Entry
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
